import { Search, Download, Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { exportCsvUrl } from "@/lib/api";

const STATUS = [
  { v: "all", l: "Tous les statuts" },
  { v: "present", l: "Présents uniquement" },
  { v: "parti", l: "Partis uniquement" },
  { v: "overstay", l: "Alertes > 4h" },
];

const PERIODS = [
  { v: "today", l: "Aujourd'hui" },
  { v: "yesterday", l: "Hier" },
  { v: "week", l: "7 derniers jours" },
  { v: "all", l: "Historique complet" },
  { v: "custom", l: "Plage personnalisée" },
];

export const Filters = ({ filter, setFilter, search, setSearch, status, setStatus, sites = [], siteId = "", setSiteId, onPrint }) => {
  const setPeriod = (period) => setFilter((f) => ({ ...f, period }));
  const csvParams = { period: filter.period };
  if (filter.period === "custom") {
    if (filter.from) csvParams.frm = filter.from;
    if (filter.to) csvParams.to = filter.to;
  }
  if (siteId) csvParams.site_id = siteId;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-4 no-print" data-testid="filters-bar">
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            className="pl-9 min-h-[46px] bg-slate-50 border-slate-300 focus-visible:ring-[#D4AF37]"
            placeholder="Rechercher par nom, société, personne visitée, badge…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-input"
          />
        </div>

        <div className="flex flex-wrap gap-3">
          {sites.length > 0 && (
            <Select value={siteId || "__all__"} onValueChange={(v) => setSiteId(v === "__all__" ? "" : v)}>
              <SelectTrigger className="min-h-[46px] w-[190px] bg-slate-50 border-slate-300" data-testid="site-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" data-testid="site-all">Tous les sites</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`site-${s.id}`}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={filter.period} onValueChange={setPeriod}>
            <SelectTrigger className="min-h-[46px] w-[180px] bg-slate-50 border-slate-300" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p.v} value={p.v} data-testid={`period-${p.v}`}>
                  {p.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="min-h-[46px] w-[180px] bg-slate-50 border-slate-300" data-testid="status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS.map((s) => (
                <SelectItem key={s.v} value={s.v} data-testid={`status-${s.v}`}>
                  {s.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2">
          <a href={exportCsvUrl(csvParams)} data-testid="export-csv-btn">
            <Button variant="outline" className="min-h-[46px] border-slate-300 hover:bg-slate-50 gap-2">
              <Download className="h-4 w-4" /> CSV
            </Button>
          </a>
          <Button
            onClick={onPrint}
            className="min-h-[46px] bg-[#D4AF37] hover:bg-[#C59B27] text-[#0A192F] font-semibold gap-2"
            data-testid="print-report-btn"
          >
            <Printer className="h-4 w-4" /> Rapport
          </Button>
        </div>
      </div>

      {filter.period === "custom" && (
        <div className="flex flex-wrap gap-3 items-center pt-1" data-testid="custom-range">
          <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Du</span>
          <Input
            type="date"
            className="min-h-[44px] w-[170px] bg-slate-50 border-slate-300"
            value={filter.from || ""}
            onChange={(e) => setFilter((f) => ({ ...f, from: e.target.value }))}
            data-testid="date-from"
          />
          <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Au</span>
          <Input
            type="date"
            className="min-h-[44px] w-[170px] bg-slate-50 border-slate-300"
            value={filter.to || ""}
            onChange={(e) => setFilter((f) => ({ ...f, to: e.target.value }))}
            data-testid="date-to"
          />
        </div>
      )}
    </div>
  );
};
